import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Input,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { PillButton } from '@/components/ui/PillButton';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { isCarouselCompleted } from '@/services/carouselStorage';
import { useAuth } from '../AuthProvider';
import { OAuthButtons } from './OAuthButtons';
import {
  startEmail,
  verifyEmailOtp,
  resendEmailOtp,
  completeProfile,
  EmailAuthError,
} from '../emailAuth';

type FlowStep = 'email' | 'otp' | 'profile';

// Zitadel's default email-OTP code is 8 digits; accept up to 10 to match the
// backend validator and auto-submit once the user has typed 8.
const OTP_AUTO_SUBMIT_AT = 8;
const OTP_MAX_DIGITS = 10;
const RESEND_COOLDOWN_SECONDS = 30;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof EmailAuthError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * In-app email-OTP sign-in / sign-up — the web counterpart of mobile's
 * EmailAuthForm + OTP-verify + profile-complete screens. One self-contained
 * component drives all three sub-steps so the user enters an email, receives a
 * code, and (for brand-new accounts) completes their profile without ever
 * leaving the SPA — no Zitadel hosted-login redirect.
 *
 * Navigation after success:
 *   - returning user → the `from` path stashed by the auth gate, else `/app`.
 *   - new user (after profile) → the onboarding carousel/wizard.
 * When rendered inside the onboarding wizard's auth step there is no `from`;
 * `refreshAuth()` flips the wizard to authenticated and it resumes itself.
 */
export interface EmailOtpFlowProps {
  /**
   * When true, drops the page chrome (full-height cream wrapper + wordmark /
   * language header) so the flow can sit inside the onboarding wizard's auth
   * step, which supplies its own chrome.
   */
  embedded?: boolean;
  /**
   * Invoked after a brand-new account finishes profile completion (the org is
   * created). When embedded in the onboarding wizard this advances the wizard to
   * its next step; the standalone /signup path leaves it unset and navigates.
   */
  onAuthComplete?: () => void;
}

export function EmailOtpFlow({ embedded = false, onAuthComplete }: EmailOtpFlowProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { refreshAuth } = useAuth();
  // Navigation state carries either the post-login destination (`from`) or, for a
  // brand-new SOCIAL user, a request to resume straight at the profile step
  // (they're already authenticated via IdP intent; they just need to create their
  // org). See IdpCallbackPage.
  const navState = location.state as {
    from?: string;
    resumeProfile?: boolean;
    prefill?: { email?: string; firstName?: string; lastName?: string };
  } | null;
  const from = navState?.from;
  const resumeProfile = navState?.resumeProfile === true;

  const [step, setStep] = useState<FlowStep>(resumeProfile ? 'profile' : 'email');
  const [busy, setBusy] = useState(false);

  // email step
  const [email, setEmail] = useState(navState?.prefill?.email ?? '');
  const [emailError, setEmailError] = useState('');

  // otp step
  const [ticketId, setTicketId] = useState('');
  const [code, setCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  // Last code handed to verify — stops the auto-submit effect from looping on a
  // rejected (or dev-prefilled) code.
  const lastAttemptedCode = useRef('');

  // profile step (pre-filled from the provider's name for a new social user)
  const [firstName, setFirstName] = useState(navState?.prefill?.firstName ?? '');
  const [lastName, setLastName] = useState(navState?.prefill?.lastName ?? '');
  const [organizationName, setOrganizationName] = useState('');
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});

  // Resend cooldown ticker.
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  const toastError = useCallback(
    (message: string) => toast({ status: 'error', description: message, duration: 8000, isClosable: true }),
    [toast],
  );

  // ── email step ────────────────────────────────────────────────────────────
  const handleStart = useCallback(async () => {
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError(t('auth.invalidEmail'));
      return;
    }
    setEmailError('');
    setBusy(true);
    try {
      const res = await startEmail(trimmed);
      setTicketId(res.ticketId);
      setCode(res.devCode ?? '');
      lastAttemptedCode.current = '';
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      setStep('otp');
    } catch (err) {
      toastError(messageFor(err, t('auth.otpFailed')));
    } finally {
      setBusy(false);
    }
  }, [email, t, toastError]);

  // ── otp step ──────────────────────────────────────────────────────────────
  const handleVerify = useCallback(
    async (codeToSubmit: string) => {
      if (!ticketId) {
        toastError(t('auth.otpTicketMissing'));
        setStep('email');
        return;
      }
      if (busy || lastAttemptedCode.current === codeToSubmit) return;
      lastAttemptedCode.current = codeToSubmit;
      setBusy(true);
      try {
        const data = await verifyEmailOtp({ ticketId, code: codeToSubmit });
        if (data.needsProfile) {
          setStep('profile');
          return;
        }
        // Returning user — tokens are persisted; surface the session and go.
        refreshAuth();
        navigate(from || '/app', { replace: true });
      } catch (err) {
        // Recoverable: clear the field so the user retypes and can retry.
        setCode('');
        lastAttemptedCode.current = '';
        toastError(messageFor(err, t('auth.otpFailed')));
      } finally {
        setBusy(false);
      }
    },
    [ticketId, busy, from, navigate, refreshAuth, t, toastError],
  );

  // Auto-submit once enough digits are typed (mirrors mobile).
  useEffect(() => {
    if (
      step === 'otp' &&
      code.length >= OTP_AUTO_SUBMIT_AT &&
      /^\d+$/.test(code) &&
      !busy &&
      lastAttemptedCode.current !== code
    ) {
      void handleVerify(code);
    }
  }, [step, code, busy, handleVerify]);

  const handleResend = useCallback(async () => {
    if (resendCountdown > 0 || !ticketId) return;
    setBusy(true);
    try {
      const res = await resendEmailOtp(ticketId);
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      setCode(res.devCode ?? '');
      lastAttemptedCode.current = '';
      toast({ status: 'success', description: t('auth.otpResent'), duration: 4000, isClosable: true });
    } catch (err) {
      toastError(messageFor(err, t('auth.otpFailed')));
    } finally {
      setBusy(false);
    }
  }, [resendCountdown, ticketId, t, toast, toastError]);

  const backToEmail = useCallback(() => {
    setStep('email');
    setCode('');
    setTicketId('');
    lastAttemptedCode.current = '';
  }, []);

  // ── profile step ──────────────────────────────────────────────────────────
  const handleCompleteProfile = useCallback(async () => {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = t('auth.requiredField');
    if (!lastName.trim()) errs.lastName = t('auth.requiredField');
    if (!organizationName.trim()) errs.organizationName = t('auth.requiredField');
    setProfileErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setBusy(true);
    try {
      await completeProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        organizationName: organizationName.trim(),
      });
      refreshAuth();
      if (onAuthComplete) {
        // Embedded in the wizard (already on /onboarding/wizard): advance the
        // wizard's own step machine — navigating to the same route is a no-op.
        onAuthComplete();
      } else {
        // Standalone /signup: brand-new account → onboarding (carousel first if not yet seen).
        navigate(isCarouselCompleted() ? '/onboarding/wizard' : '/onboarding', { replace: true });
      }
    } catch (err) {
      toastError(messageFor(err, t('auth.otpFailed')));
    } finally {
      setBusy(false);
    }
  }, [firstName, lastName, organizationName, navigate, onAuthComplete, refreshAuth, t, toastError]);

  const inputStyles = {
    bg: 'white',
    borderColor: 'blackAlpha.200',
    borderRadius: '12px',
    h: '48px',
    _hover: { borderColor: 'blackAlpha.300' },
    _focusVisible: { borderColor: 'onboarding.plum', boxShadow: 'none' },
  } as const;

  const content = (
    <VStack
      w="100%"
      maxW="420px"
      mx="auto"
      align="stretch"
      spacing={6}
      mt={embedded ? 0 : { base: 4, md: 12 }}
      data-testid="email-otp-flow"
    >
      {!embedded && (
        <HStack justify="space-between" align="center">
          <Heading
            as="span"
            fontFamily="'Inter', sans-serif"
            fontWeight="700"
            fontSize="22px"
            color="onboarding.plum"
          >
            Streamko
          </Heading>
          <LanguageSelector
            currentLocale={i18n.language || 'en'}
            onLocaleChange={(locale) => i18n.changeLanguage(locale)}
            testID="auth-language-selector"
          />
        </HStack>
      )}

        {step === 'email' && (
          <VStack align="stretch" spacing={5}>
            <VStack align="stretch" spacing={1}>
              <Text fontSize="20px" fontWeight="600" color="onboarding.plum">
                {t('auth.entryTitle')}
              </Text>
              <Text fontSize="14px" color="onboarding.mutedRose" lineHeight="20px">
                {t('auth.entrySubtitle')}
              </Text>
            </VStack>

            <VStack align="stretch" spacing={2}>
              <Text fontSize="sm" fontWeight="500" color="onboarding.plum">
                {t('auth.email')}
              </Text>
              <Input
                {...inputStyles}
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoComplete="email"
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleStart();
                }}
                isInvalid={!!emailError}
                data-testid="auth-email-input"
              />
              {emailError && (
                <Text fontSize="xs" color="red.500">
                  {emailError}
                </Text>
              )}
            </VStack>

            <PillButton
              label={t('auth.entrySubmit')}
              variant="primary"
              isLoading={busy}
              disabled={busy}
              onPress={handleStart}
              testID="auth-submit"
            />

            <HStack align="center" spacing={3}>
              <Divider borderColor="onboarding.mutedRose" opacity={0.4} />
              <Text fontSize="13px" color="onboarding.mutedRose" whiteSpace="nowrap">
                {t('auth.orContinueWith')}
              </Text>
              <Divider borderColor="onboarding.mutedRose" opacity={0.4} />
            </HStack>

            <OAuthButtons disabled={busy} />
          </VStack>
        )}

        {step === 'otp' && (
          <VStack align="stretch" spacing={6}>
            <VStack align="stretch" spacing={1}>
              <Text fontSize="20px" fontWeight="600" color="onboarding.plum">
                {t('auth.otpTitle')}
              </Text>
              <Text fontSize="14px" color="onboarding.mutedRose" lineHeight="20px">
                {t('auth.otpSubtitle', { email })}
              </Text>
            </VStack>

            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, OTP_MAX_DIGITS))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={OTP_MAX_DIGITS}
              autoFocus
              textAlign="center"
              fontSize="28px"
              fontWeight="600"
              letterSpacing="8px"
              color="onboarding.plum"
              bg="white"
              borderColor="blackAlpha.200"
              borderRadius="12px"
              h="64px"
              _hover={{ borderColor: 'blackAlpha.300' }}
              _focusVisible={{ borderColor: 'onboarding.plum', boxShadow: 'none' }}
              placeholder="••••••••"
              data-testid="otp-code-input"
            />

            <VStack spacing={3} align="center">
              <Text
                as="button"
                type="button"
                fontSize="14px"
                fontWeight={resendCountdown > 0 ? '400' : '600'}
                color={resendCountdown > 0 ? 'onboarding.mutedRose' : 'onboarding.plum'}
                onClick={handleResend}
                cursor={resendCountdown > 0 || busy ? 'default' : 'pointer'}
                opacity={resendCountdown > 0 || busy ? 0.7 : 1}
                data-testid="otp-resend"
              >
                {resendCountdown > 0
                  ? t('auth.otpResendCountdown', { seconds: resendCountdown })
                  : t('auth.otpResend')}
              </Text>

              <PillButton
                label={t('auth.changeEmail')}
                variant="secondary"
                disabled={busy}
                onPress={backToEmail}
                testID="otp-change-email"
              />
            </VStack>
          </VStack>
        )}

        {step === 'profile' && (
          <VStack align="stretch" spacing={6}>
            <VStack align="stretch" spacing={1}>
              <Text fontSize="20px" fontWeight="600" color="onboarding.plum">
                {t('auth.profileTitle')}
              </Text>
              <Text fontSize="14px" color="onboarding.mutedRose" lineHeight="20px">
                {t('auth.profileSubtitle', { email })}
              </Text>
            </VStack>

            <HStack align="flex-start" spacing={3}>
              <VStack align="stretch" spacing={2} flex={1}>
                <Text fontSize="sm" fontWeight="500" color="onboarding.plum">
                  {t('auth.firstName')}
                </Text>
                <Input
                  {...inputStyles}
                  autoComplete="given-name"
                  placeholder={t('auth.firstNamePlaceholder')}
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (profileErrors.firstName) setProfileErrors((p) => ({ ...p, firstName: '' }));
                  }}
                  isInvalid={!!profileErrors.firstName}
                  data-testid="profile-firstname-input"
                />
              </VStack>
              <VStack align="stretch" spacing={2} flex={1}>
                <Text fontSize="sm" fontWeight="500" color="onboarding.plum">
                  {t('auth.lastName')}
                </Text>
                <Input
                  {...inputStyles}
                  autoComplete="family-name"
                  placeholder={t('auth.lastNamePlaceholder')}
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (profileErrors.lastName) setProfileErrors((p) => ({ ...p, lastName: '' }));
                  }}
                  isInvalid={!!profileErrors.lastName}
                  data-testid="profile-lastname-input"
                />
              </VStack>
            </HStack>

            <VStack align="stretch" spacing={2}>
              <Text fontSize="sm" fontWeight="500" color="onboarding.plum">
                {t('auth.organizationName')}
              </Text>
              <Input
                {...inputStyles}
                autoComplete="organization"
                placeholder={t('auth.organizationNamePlaceholder')}
                value={organizationName}
                onChange={(e) => {
                  setOrganizationName(e.target.value);
                  if (profileErrors.organizationName) setProfileErrors((p) => ({ ...p, organizationName: '' }));
                }}
                isInvalid={!!profileErrors.organizationName}
                data-testid="profile-orgname-input"
              />
            </VStack>

            <PillButton
              label={t('auth.profileSubmit')}
              variant="primary"
              isLoading={busy}
              disabled={busy}
              onPress={handleCompleteProfile}
              testID="profile-submit"
            />
          </VStack>
        )}
    </VStack>
  );

  if (embedded) return content;

  return (
    <Box
      minH="100vh"
      bg="onboarding.cream"
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      px={5}
      py={6}
    >
      {content}
    </Box>
  );
}

export default EmailOtpFlow;
